import {
  EPermission,
  FolderResponse,
  MediaSequenceCreateNewRequest,
  UserSimpleResponse,
} from 'api/core';
import { useSearchFolders } from 'api/useFoldersApi';
import { useSearchUsers } from 'api/useUsersApi';
import { PermissionProtectedComponent } from 'auth/PermissionProtectedComponent';
import {
  AddressSuggestion,
  AutocompleteAddress,
} from 'components/Form/AutocompleteAddress';
import { LabelWithHelperText } from 'components/Form/LabelWithHelperText';
import { EntitySelectSingleAsync } from 'components/Select/EntitySelectSingleAsync';
import { useFormContext } from 'react-hook-form';

interface MediaSequenceCreateFormConfigurationStepProps {
  inDialog?: boolean;
  setSelectedSuggestion: (suggestion: AddressSuggestion | undefined) => void;
  selectedMetaMappingUser?: UserSimpleResponse;
  setSelectedMetaMappingUser: (user: UserSimpleResponse | undefined) => void;
  selectedFolder?: FolderResponse;
  setSelectedFolder: (folder: FolderResponse | undefined) => void;
}

export const MediaSequenceCreateFormConfigurationStep = ({
  inDialog,
  setSelectedSuggestion,
  selectedMetaMappingUser,
  setSelectedMetaMappingUser,
  selectedFolder,
  setSelectedFolder,
}: MediaSequenceCreateFormConfigurationStepProps) => {
  const { register, watch, setValue } =
    useFormContext<MediaSequenceCreateNewRequest>();

  const onUserSelected = (userResponse: UserSimpleResponse | null) => {
    setSelectedMetaMappingUser(userResponse ?? undefined);
    setValue('metaMappingUserId', userResponse?.id, { shouldValidate: true });
  };

  const onFolderSelected = (folderResponse: FolderResponse | null) => {
    setSelectedFolder(folderResponse ?? undefined);
    setValue('folderId', folderResponse?.id, { shouldValidate: true });
  };

  return (
    <>
      <AutocompleteAddress
        label={
          <LabelWithHelperText
            label={
              <p>
                Adresse (eller titel) <span className="text-red-500">*</span>
              </p>
            }
            helperText="Indtast en adresse for at hente stamdata, eller en titel for at bruge den som videonavn."
            inDialog={inDialog}
          />
        }
        wrapLabelInLabelTag={false}
        registration={() => register('name', { required: true })}
        formWatch={watch('name') || ''}
        formSetValue={(value: string) =>
          setValue('name', value, { shouldValidate: true })
        }
        inputClassName="input input-bordered"
        onSuggestionSelectedFn={setSelectedSuggestion}
      />

      <PermissionProtectedComponent
        permissions={[EPermission.UserSimpleSearch]}
      >
        <div className="form-control">
          <LabelWithHelperText
            label={<p>Tilknyt mægler</p>}
            helperText="Der bliver automatisk indhentet stamdata for den valgte mægler."
            inDialog={inDialog}
          />
          <EntitySelectSingleAsync<UserSimpleResponse, { searchTerm?: string }>
            id="media-sequence-meta-mapping-user-select"
            useSearchFunction={useSearchUsers}
            searchFunctionOrder={(a, b) => a.name.localeCompare(b.name)}
            searchParams={{}}
            renderSuggestion={(userSuggestion) => (
              <span
                data-testid={`media-sequence-meta-mapping-user-select-${userSuggestion.id}`}
              >
                {userSuggestion.name}{' '}
                {userSuggestion.title ? `(${userSuggestion.title})` : undefined}
              </span>
            )}
            initialValue={selectedMetaMappingUser}
            onSuggestionSelected={onUserSelected}
            inDialog={inDialog}
          />

          <input
            {...register('metaMappingUserId')}
            type="text"
            className="input input-bordered hidden"
          />
        </div>
      </PermissionProtectedComponent>

      <PermissionProtectedComponent permissions={[EPermission.FolderRead]}>
        <div className="form-control">
          <LabelWithHelperText
            label={<p>Tilknyt mappe</p>}
            helperText="Vælg hvilken mappe videoen skal tilknyttes."
            inDialog={inDialog}
          />
          <EntitySelectSingleAsync<FolderResponse, { searchTerm?: string }>
            id="media-sequence-folder-select"
            useSearchFunction={useSearchFolders}
            searchFunctionOrder={(a, b) => a.name.localeCompare(b.name)}
            searchParams={{}}
            renderSuggestion={(folderSuggestion) => (
              <span
                data-testid={`media-sequence-folder-select-${folderSuggestion.id}`}
              >
                {folderSuggestion.name}
              </span>
            )}
            initialValue={selectedFolder}
            onSuggestionSelected={onFolderSelected}
            inDialog={inDialog}
          />

          <input
            {...register('folderId')}
            type="text"
            className="input input-bordered hidden"
          />
        </div>
      </PermissionProtectedComponent>
    </>
  );
};
